import { Injectable } from '@angular/core'
import { AngularFireFunctions } from '@angular/fire/compat/functions'

export interface StoredImageInfo {
  url: string
  path: string
}

type ProgressHandler = (percent: number) => void

const IMAGE_FUNCTIONS = {
  upload: 'uploadeventimage',
  cloneFromUrl: 'cloneeventimagefromurl',
  cloneFromPath: 'cloneeventimagefrompath',
  delete: 'deleteeventimage',
} as const

type ImageFunctionName = typeof IMAGE_FUNCTIONS[keyof typeof IMAGE_FUNCTIONS]

@Injectable({
  providedIn: 'root',
})
export class ImageStorageService {
  private readonly functionNames = IMAGE_FUNCTIONS

  constructor(private cloudFunctions: AngularFireFunctions) {}

  async uploadBase64Image(
    base64: string,
    organiserId: string,
    eventId?: string | null,
    onProgress?: ProgressHandler
  ): Promise<StoredImageInfo> {
    const payload = {
      base64Image: base64,
      organiserId,
      eventId: eventId ?? undefined,
    }

    return this.runWithProgress(
      () => this.callImageFunction<StoredImageInfo>(this.functionNames.upload, payload),
      onProgress
    )
  }

  async cloneFromUrl(imageUrl: string, organiserId: string, eventId?: string | null): Promise<StoredImageInfo> {
    const payload = { url: imageUrl, organiserId, eventId: eventId ?? undefined }
    return this.callImageFunction<StoredImageInfo>(this.functionNames.cloneFromUrl, payload)
  }

  async cloneFromPath(
    imageStoragePath: string,
    organiserId: string,
    eventId?: string | null
  ): Promise<StoredImageInfo> {
    const payload = { path: imageStoragePath, organiserId, eventId: eventId ?? undefined }
    return this.callImageFunction<StoredImageInfo>(this.functionNames.cloneFromPath, payload)
  }

  deleteByPath(path: string): Promise<void> {
    return this.callImageFunction<void>(this.functionNames.delete, { path })
  }

  private async callImageFunction<TResponse>(
    name: ImageFunctionName,
    payload: Record<string, unknown>
  ): Promise<TResponse> {
    const callable = this.cloudFunctions.httpsCallable<Record<string, unknown>, TResponse>(name)
    return callable(payload).toPromise() as Promise<TResponse>
  }

  private async runWithProgress<T>(operation: () => Promise<T>, onProgress?: ProgressHandler): Promise<T> {
    this.emitProgress(onProgress, 0)
    const result = await operation()
    this.emitProgress(onProgress, 100)
    return result
  }

  private emitProgress(onProgress: ProgressHandler | undefined, value: number): void {
    if (onProgress) {
      onProgress(Math.round(value))
    }
  }
}
